import React, { useContext, useEffect, useState } from 'react';
import Header from '../../../components/Common/Header/Header';
import ContentApp from '../../../components/ContentApp';
import { WelletContext } from '../../../context/WelletContext';
import { getQueryString } from '../../../helpers/url';
import { useDispatch, useSelector } from 'react-redux';
import {
  addExtraProduct,
  removeExtraProduct,
  setExtraProductList,
} from '../../../actions/extraProductActions';
import { useTranslation } from 'react-i18next';
import ButtonReservation from '../../../components/Common/Button/ButtonReservation';
import Money from '../../../components/Common/Money/Money';
import { setReservation } from '../../../actions/reservationActions';
import ChevronIcon from '../../../components/icons/chevronIcon';
import styles from '../ReservationCheck/reservationCheck.module.css';
import { CircleSpinner } from 'react-spinners-kit';
import EventHeader from '../../../components/Common/EventHeader/EventHeader';
import ProductModal from '../../../components/Common/ProductModal/ProductModal';
import ReservationTicketInfo from '../ReservationTicketInfo/ReservationTicketInfo';
import { fetchProducts } from '../../../services/product/productService';
import ProductCard from '../../../components/ProductCard/ProductCard';
import SkeletonReservationArea from '../../../components/Skeletons/SkeletonReservationArea';
import CurrencySelectorWithPicker from '../../../components/CurrencySelector/CurrencySelectorWithPicker';
import DatePickerSlider from '../../../components/DatePickerSlider/DatePickerSlider';
import { format, addDays, parseISO, isSameDay } from 'date-fns';
import { getDateFromOffset, getLocale } from '../../../helpers/dates';
import DatePickerSliderNotEvents from '../../../components/DatePickerSlider/DatePickerSliderNotEvents';
import ErrorCard from '../../../components/ErrorCard/ErrorCard';
import usePurchaseRules from '../../../hooks/usePurchaseRules';

const CalendarTicketSelectorGroup = ({ history }) => {
  const { t } = useTranslation();
  const language = useSelector((state) => state.app.language);
  const locale = getLocale(language);
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);

  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [groupProducts, setGroupProducts] = useState([]);
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingImage, setLoadingImage] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dates, setDates] = useState([]);
  const [datesCalendar, setDatesCalendar] = useState([]);
  const idReservation = getQueryString('id');
  const showId = getQueryString('showId');
  const extraProducts = useSelector((state) => state.extraProducts.items);
  const { ruleError, ruleErrorRef, validateRules, clearRuleErrorIfSatisfied, clearRuleError } =
    usePurchaseRules(products);

  const queryParams = new URLSearchParams({
    showId: showId,
  });

  if (idReservation !== null && idReservation !== undefined) {
    queryParams.append('id', idReservation);
  }

  let query = `?${queryParams.toString()}`;
  const generateSwiperDates = () => {
    const today = new Date();
    const swiperDates = Array.from({ length: 11 }, (_, i) => ({
      date: addDays(today, i),
      hasEvents: true,
    }));
    return swiperDates;
  };

  const generateCalendarDates = () => {
    const today = new Date();
    const endDate = addDays(today, 365);
    const calendarDates = [];
    let currentDate = today;
    while (currentDate <= endDate) {
      calendarDates.push({
        date: new Date(currentDate),
        hasEvents: true,
      });
      currentDate = addDays(currentDate, 1);
    }
    return calendarDates;
  };

  const getShow = async () => {
    try {
      const result = await welletContext.apis.tickets.get(`/shows/get/`, {
        params: {
          showId: showId,
          lang: language,
          details: false,
          reservationType: 'RESTAURANT',
          images: true,
          videos: false,
          IsFeature: true,
        },
      });

      setShow(result.data);
      const date = format(new Date(getDateFromOffset(result.data.utcOffset)), 'yyyy-MM-dd');
      setSelectedDate(date);
      setDates(generateSwiperDates());
      setDatesCalendar(generateCalendarDates());
      getProducts(date);
    } catch (error) {
      console.error(error);
      setError(t('callApiError'));
    } finally {
      setLoading(false);
    }
  };

  const getProducts = async (date) => {
    setLoadingProducts(true);
    setError(null);

    try {
      const result = await fetchProducts(
        welletContext,
        showId,
        date,
        language,
        null,
        null,
        'GROUP',
      );

      if (result && result.data && result.data.products.length > 0) {
        let initializedProducts = result.data.products.map((product) => ({
          ...product,
          quantity: 0,
          disabled: false,
        }));
        const menuMain = initializedProducts.filter((p) => p.specialType === 'MENU_MAIN');
        const menuSpecial = initializedProducts.filter((p) => p.specialType === 'MENU_SPECIAL');
        const finalProducts = [...menuMain, ...menuSpecial];

        setProducts(finalProducts);
        dispatch(setExtraProductList(result.data.extraProducts));
      } else {
        setError(t('productsNotAvailables'));
      }
    } catch (error) {
      setError(
        error.response && error.response.data ? t('reservationNotAvailables') : t('callApiError'),
      );
      console.error(error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleSelectDate = (date) => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    const formattedDate = format(parsedDate, 'yyyy-MM-dd');
    const existsInSlider = dates.some((d) => isSameDay(d.date, parsedDate));

    if (!existsInSlider) {
      const newDates = Array.from({ length: 10 }, (_, i) => ({
        date: addDays(parsedDate, i),
        hasEvents: true,
      }));

      setDates(newDates);
    }

    setSelectedDate(formattedDate);
    clearRuleError();
    getProducts(formattedDate);
  };

  const handleIncrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    const product = products[index];

    let newProducts = [...products];
    if (product.isExclusive) {
      newProducts
        .filter((p) => p.isExclusive && p.id !== product.id)
        .forEach((p) => (p.disabled = true));
    }

    newProducts[index].disabled = false;

    let newQuantity = newProducts[index].quantity;

    if (newQuantity == 0) newQuantity = newProducts[index].minQuantity;
    else newQuantity += 1;

    newProducts[index].quantity = newProducts[index].stock
      ? Math.min(newProducts[index].stock, newQuantity)
      : newQuantity;

    setProducts(newProducts);

    clearRuleErrorIfSatisfied(newProducts);

    if (product.extraPax > 0) {
      dispatch(addExtraProduct({ ...product, quantity: 0, specialType: 'EXTRAPAX' }));
    }
  };

  const handleDecrement = (id) => {
    const index = products.findIndex((p) => p.id === id);
    let newProducts = [...products];
    let newQuantity = newProducts[index].quantity - 1;
    if (newQuantity < newProducts[index].minQuantity) {
      newQuantity = 0;
    }
    newProducts[index].quantity = Math.max(0, newQuantity);
    if (newProducts[index].isExclusive && newProducts[index].quantity === 0) {
      const anyExclusiveSelected = newProducts.some((p) => p.isExclusive && p.quantity > 0);
      if (!anyExclusiveSelected) {
        newProducts.forEach((p) => {
          if (p.isExclusive) p.disabled = false;
        });
      }
    }

    dispatch(removeExtraProduct({ ...newProducts[index], quantity: 0, specialType: 'EXTRAPAX' }));
    setProducts(newProducts);

    clearRuleErrorIfSatisfied(newProducts);
  };


  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const onInfoClick = (product) => {
    setSelectedProduct(product);
    toggleModal();
  };

  const handleContinue = () => {
    if (!validateRules()) return;

    const reservationInfo = setReservationInfo();
    dispatch(setReservation(reservationInfo));

    if (extraProducts.length > 0) {
      history.push({
        pathname: '/area-extra-reservation-group',
        search: query,
      });
    } else {
      history.push({
        pathname: '/reservation-check-groups',
        search: query,
      });
    }
  };

  const setReservationInfo = () => {
    let finalProducts = [];

    products
      .filter((product) => product.quantity > 0)
      .map((product) => {
        finalProducts.push({
          id: product.id,
          quantity: product.quantity,
          extraPaxs: 0,
          specialType: product.specialType,
          paxs: product.pax * product.quantity,
          ageRange: product.ageRange,
        });
      });

    const reservationInfo = {
      showId: showId,
      showName: show.name,
      products: finalProducts,
      date: selectedDate,
      paxs: finalProducts.reduce((sum, item) => sum + item.paxs, 0),
    };

    return reservationInfo;
  };

  const sumTotal = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.price * p.quantity, 0);
  };

  const totalConsume = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.prices.consumption * p.quantity, 0);
  };

  const totalPax = () => {
    return products
      .filter((p) => p.quantity > 0)
      .reduce((total, p) => total + p.pax * p.quantity, 0);
  };

  const renderProducts = () => {
    if (loadingProducts) {
      return SkeletonReservationArea();
    }
    const menuMain = products.filter((p) => p.specialType === 'MENU_MAIN');
    const menuSpecial = products.filter((p) => p.specialType === 'MENU_SPECIAL');
    return (
      <div className='product-list mt-3'>
        <div className='fw-600 font-small '>{t('selectMenu')}</div>
        {menuMain.map((p) => (
          <div className='mt-2' key={p.id}>
            <ProductCard
              product={p}
              handleIncrement={() => handleIncrement(p.id)}
              handleDecrement={() => handleDecrement(p.id)}
              hasMoreInfo={true}
              onInfoClick={() => onInfoClick(p)}
              loadingImage={loadingImage}
              setLoadingImage={setLoadingImage}
            />
          </div>
        ))}

        {menuSpecial.length > 0 && (
          <div className='fw-600 font-small mt-3'>{t('specialMenus')}</div>
        )}
        {menuSpecial.map((p) => (
          <div className='mt-2' key={p.id}>
            <ProductCard
              product={p}
              handleIncrement={() => handleIncrement(p.id)}
              handleDecrement={() => handleDecrement(p.id)}
              hasMoreInfo={true}
              onInfoClick={() => onInfoClick(p)}
              loadingImage={loadingImage}
              setLoadingImage={setLoadingImage}
            />
          </div>
        ))}
      </div>
    );
  };

  const hasItems = products?.some((p) => p.quantity > 0);

  useEffect(() => {
    getShow();
  }, []);

  return (
    <>
      {loading ? (
        <div className='loaderDiv'>
          <CircleSpinner
            className='spinner-app mx-auto'
            style={{ margin: 'auto' }}
            size={40}
            color='var(--color-font-primary)'
          />
        </div>
      ) : (
        <>
          <Header />
          <ContentApp>
            <EventHeader
              eventImg={show.bannerImageUrl}
              eventName={show.name}
              eventDescription={show.city.name}
              size='small'
            />
            <div className='mt-3'>
              <DatePickerSliderNotEvents
                dates={dates}
                firstDate={new Date()}
                handleSelect={(e) => handleSelectDate(e)}
                datesCalendar={datesCalendar}
                minDate={new Date()}
              />
            </div>
            <div className='mt-3'>{renderProducts()}</div>
            {ruleError && (
              <div className='mt-3' ref={ruleErrorRef}>
                <ErrorCard
                  title={ruleError.map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                />
              </div>
            )}
            <div className='mt-3'>
              <CurrencySelectorWithPicker />
            </div>
            {hasItems && (
              <div
                className='position-fixed w-100'
                style={{
                  bottom: 0,
                  left: 0,
                  zIndex: 2,
                }}
              >
                <div className='container'>
                  <div className='row justify-content-center'>
                    <div className='col-12 col-md-7 col-lg-6 p-0'>
                      <ButtonReservation
                        leftContent={
                          <>
                            <div className='font-normal'>
                              {t('total')} <Money value={sumTotal()} />
                            </div>
                            <div className='font-smaller'>{t('person', { count: totalPax() })}</div>
                            {totalConsume() > 0 && (
                              <div className='font-smaller'>
                                {t('consumptionCredit')} <Money value={totalConsume()} />
                              </div>
                            )}
                          </>
                        }
                        rightContent={
                          <div>
                            {t('continue')}
                            <span>
                              <ChevronIcon size={20} fillColor={'#000'} />
                            </span>
                          </div>
                        }
                        size='large'
                        className={`${styles.btn}`}
                        onClick={handleContinue}
                        fixed={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </ContentApp>
          <ProductModal
            open={isModalOpen}
            onClose={toggleModal}
            animationClass='slide-left-right'
            fullScreen={true}
            hasBackground={true}
            backIcon={true}
          >
            <ReservationTicketInfo
              productId={selectedProduct?.id}
              showAddButton={
                selectedProduct?.stock === null ||
                (!selectedProduct?.hasSoldOut && selectedProduct?.stock > 0)
              }
              handleIncrement={handleIncrement}
              prices={selectedProduct?.prices}
              toggleModal={toggleModal}
              isModalOpen={isModalOpen}
            />
          </ProductModal>
        </>
      )}
    </>
  );
};

export default CalendarTicketSelectorGroup;
